import { directoryMetadata, directoryListSchema } from "../utils/resourceSeo";
import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import Nav from "../components/Nav";
import ResourceReportDialog from "../components/ResourceReportDialog";
import {
  resources,
  counties,
  resourceCategories,
  subcategoryGuidance,
} from "../data/resources";
import SEO, {
  generateWebPageSchema,
  generateFAQSchema,
} from "../components/SEO";
import {
  faqSections,
  getCountyFromSlug,
  getResourceCategoryBySlug,
  getResourceCategoryCountyUrl,
  getResourceCategoryUrl,
  getResourceCountyUrl,
  toAbsoluteUrl,
} from "../utils/seoPaths";

const Resources = () => {
  const location = useLocation();
  const { categorySlug, countySlug } = useParams();

  const [selectedCounty, setSelectedCounty] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get("category");
    const countyParam = searchParams.get("county");
    const searchParam = searchParams.get("search");

    const pathCategory = categorySlug
      ? getResourceCategoryBySlug(categorySlug)?.label
      : null;
    const pathCounty = countySlug ? getCountyFromSlug(countySlug, counties) : null;

    const normalizedCategory =
      pathCategory ||
      (categoryParam && resourceCategories.includes(categoryParam)
        ? categoryParam
        : "All");
    const normalizedCounty =
      pathCounty ||
      (countyParam && counties.includes(countyParam) ? countyParam : "All");

    setSelectedCategory(normalizedCategory);
    setSelectedCounty(normalizedCounty);
    setExpandedCategories(
      normalizedCategory !== "All" ? new Set([normalizedCategory]) : new Set()
    );

    if (searchParam) {
      setSearchTerm(decodeURIComponent(searchParam));
      return;
    }
    setSearchTerm("");
  }, [categorySlug, countySlug, location.search]);

  // Filter resources based on selections
  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      // Match either a verified statewide service area or a physical county.
      const countyMatch =
        selectedCounty === "All" ||
        resource.statewide === true ||
        resource.counties.includes(selectedCounty);

      // Category filter
      const categoryMatch =
        selectedCategory === "All" ||
        (Array.isArray(resource.category)
          ? resource.category.includes(selectedCategory)
          : resource.category === selectedCategory);

      // Search filter
      const searchMatch =
        searchTerm === "" ||
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.services.some((service) =>
          service.toLowerCase().includes(searchTerm.toLowerCase())
        );

      return countyMatch && categoryMatch && searchMatch;
    });
  }, [selectedCounty, selectedCategory, searchTerm]);

  // Group resources by category and subcategory
  const groupedResources = useMemo(() => {
    const grouped = {};
    filteredResources.forEach((resource) => {
      const categories = Array.isArray(resource.category)
        ? resource.category
        : [resource.category];
      const resourceSubcategories = Array.isArray(resource.subcategory)
        ? resource.subcategory
        : resource.subcategory
          ? [resource.subcategory]
          : [];

      categories.forEach((category) => {
        if (!grouped[category]) {
          grouped[category] = {};
        }

        let subKeys = [""];
        if (category === "Primary SUD Providers") {
          subKeys = resourceSubcategories.filter((subcategory) =>
            ["Detox", "Treatment Centers", "Respite", "RCO", "PSC"].includes(
              subcategory
            )
          );
        } else if (category === "Housing") {
          subKeys = resourceSubcategories.filter((subcategory) =>
            [
              "Shelters",
              "General Housing",
              "Recovery Homes",
              "Homelessness",
            ].includes(subcategory)
          );
        }

        if (subKeys.length === 0) {
          subKeys = [""];
        }

        subKeys.forEach((subKey) => {
          if (!grouped[category][subKey]) {
            grouped[category][subKey] = [];
          }
          grouped[category][subKey].push(resource);
        });
      });
    });

    // Sort resources alphabetically by name within each subcategory
    Object.keys(grouped).forEach((category) => {
      Object.keys(grouped[category]).forEach((subKey) => {
        grouped[category][subKey].sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
      });
    });

    return grouped;
  }, [filteredResources]);

  // When searching, auto-expand all categories and subcategories so results are visible
  useEffect(() => {
    if (searchTerm) {
      const allCategories = new Set(Object.keys(groupedResources));
      setExpandedCategories(allCategories);

      const allSubcategories = new Set();
      Object.entries(groupedResources).forEach(([category, subcategories]) => {
        Object.keys(subcategories).forEach((sub) => {
          if (sub) {
            allSubcategories.add(`${category}-${sub}`);
          }
        });
      });
      setExpandedSubcategories(allSubcategories);
    }
  }, [searchTerm, groupedResources]);

  // Tell prerender we're ready for non-async pages
  useEffect(() => {
    const id = setTimeout(() => {
      window.prerenderReady = true;
    }, 0); // let React/SEO commit meta tags first
    return () => clearTimeout(id);
  }, []);

  const toggleCard = (id) => {
    // Store current scroll position
    const currentScrollY = window.scrollY;

    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const toggleCategory = (category) => {
    // Store current scroll position
    const currentScrollY = window.scrollY;

    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const toggleSubcategory = (categorySubcategoryKey) => {
    // Store current scroll position
    const currentScrollY = window.scrollY;

    const newExpanded = new Set(expandedSubcategories);
    if (newExpanded.has(categorySubcategoryKey)) {
      newExpanded.delete(categorySubcategoryKey);
    } else {
      newExpanded.add(categorySubcategoryKey);
    }
    setExpandedSubcategories(newExpanded);

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  // Function to get display name for subcategories
  const getSubcategoryDisplayName = (subcategory) => {
    const subcategoryDisplayNames = {
      RCO: "Recovery Community Organizations",
      PSC: "Peer Support Centers",
    };
    return subcategoryDisplayNames[subcategory] || subcategory;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    // Remove any non-digit characters and format
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phone; // Return original if not standard format
  };

  const ResourceCard = ({ resource }) => {
    const isExpanded = expandedCards.has(resource.id);
    const hasMultiplePhones =
      resource.phone2 || resource.phone3 || resource.phone4;
    const hasContactInfo =
      resource.phone || hasMultiplePhones || resource.website || resource.email;

    return (
      <div id={`resource-${resource.id}`} className="bg-gray-800 border border-gray-600 rounded-lg p-4 hover:border-cyan-500 transition-colors">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h4 className="text-xl font-bold text-white mb-1">
              {resource.name}
            </h4>
            {resource.address && (
              <p className="text-gray-400 text-sm mb-2">{resource.address}</p>
            )}
          </div>
          {hasContactInfo && (
            <button
              onClick={() => toggleCard(resource.id)}
              className="ml-4 bg-cyan-500 hover:bg-cyan-400 text-gray-900 px-3 py-1 rounded text-sm font-semibold transition-colors"
            >
              {isExpanded ? "Hide Details" : "Contact Info"}
            </button>
          )}
        </div>

        {/* Services Tags */}
        {resource.services.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {resource.services.map((service, index) => (
              <span
                key={index}
                className="bg-gray-700 text-cyan-400 px-2 py-1 rounded text-xs font-medium"
              >
                {service}
              </span>
            ))}
          </div>
        )}

        {/* Description */}
        {resource.description && (
          <p className="text-gray-300 text-sm mb-3 leading-relaxed">
            {resource.description}
          </p>
        )}

        {/* Expanded Contact Details */}
        {isExpanded && (
          <div className="border-t border-gray-600 pt-3 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Phone Numbers */}
              <div>
                {resource.phone && (
                  <div className="mb-2">
                    <span className="text-cyan-400 font-semibold text-sm">
                      Phone:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone}`}
                      className="text-white hover:text-cyan-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone)}
                    </a>
                  </div>
                )}
                {resource.phone2 && (
                  <div className="mb-2">
                    <span className="text-cyan-400 font-semibold text-sm">
                      Phone 2:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone2}`}
                      className="text-white hover:text-cyan-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone2)}
                    </a>
                  </div>
                )}
                {resource.phone3 && (
                  <div className="mb-2">
                    <span className="text-cyan-400 font-semibold text-sm">
                      Phone 3:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone3}`}
                      className="text-white hover:text-cyan-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone3)}
                    </a>
                  </div>
                )}
                {resource.phone4 && (
                  <div className="mb-2">
                    <span className="text-cyan-400 font-semibold text-sm">
                      Phone 4:{" "}
                    </span>
                    <a
                      href={`tel:${resource.phone4}`}
                      className="text-white hover:text-cyan-400 transition-colors"
                    >
                      {formatPhoneNumber(resource.phone4)}
                    </a>
                  </div>
                )}
              </div>

              {/* Other Contact Info */}
              <div>
                {resource.website && (
                  <div className="mb-2">
                    <span className="text-cyan-400 font-semibold text-sm">
                      Website:{" "}
                    </span>
                    <a
                      href={resource.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-cyan-400 transition-colors break-all"
                    >
                      Visit Website →
                    </a>
                  </div>
                )}
                {resource.email && (
                  <div className="mb-2">
                    <span className="text-cyan-400 font-semibold text-sm">
                      Email:{" "}
                    </span>
                    <a
                      href={`mailto:${resource.email}`}
                      className="text-white hover:text-cyan-400 transition-colors break-all"
                    >
                      {resource.email}
                    </a>
                  </div>
                )}
                {(resource.statewide ||
                  resource.counties.some((county) => county !== "All")) && (
                  <div className="mb-2">
                    <span className="text-cyan-400 font-semibold text-sm">
                      Serves:{" "}
                    </span>
                    <span className="text-gray-300 text-sm">
                      {resource.statewide
                        ? `Statewide${
                            resource.counties.some(
                              (county) => county !== "All"
                            )
                              ? `; in-person locations: ${resource.counties
                                  .filter((county) => county !== "All")
                                  .join(", ")}`
                              : ""
                          }`
                        : resource.counties
                            .filter((county) => county !== "All")
                            .join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {resource.notes && (
              <div className="mt-3 p-3 bg-gray-700 rounded border-l-4 border-cyan-500">
                <span className="text-cyan-400 font-semibold text-sm">
                  Note:{" "}
                </span>
                <span className="text-gray-300 text-sm">{resource.notes}</span>
              </div>
            )}
          </div>
        )}
        <ResourceReportDialog
          resource={resource}
          directory="general"
          accent="cyan"
        />
      </div>
    );
  };

  // Determine if categories should be collapsible
  const shouldUseCollapsibleCategories = selectedCategory === "All";
  const showingCategoryLanding = selectedCategory !== "All";
  const showingCountyLanding = selectedCounty !== "All";
  const { title: seoTitle, description: seoDescription, heading: pageHeading, path: canonicalPath } =
    directoryMetadata(selectedCategory, selectedCounty);

  const canonicalUrl = toAbsoluteUrl(canonicalPath);
  const breadcrumbs = [
    { name: "Home", url: "https://www.recoverycompassnh.com/" },
    {
      name: "Resources",
      url: "https://www.recoverycompassnh.com/resources",
    },
  ];

  if (showingCategoryLanding) {
    breadcrumbs.push({ name: selectedCategory, url: canonicalUrl });
  }
  if (showingCountyLanding) {
    breadcrumbs.push({
      name: `${selectedCounty} County`,
      url: canonicalUrl,
    });
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
        ogType="website"
        ogImage="https://www.recoverycompassnh.com/og-resources.png"
        ogImageAlt="Recovery treatment centers and resources directory for New Hampshire"
        breadcrumbs={breadcrumbs}
        schema={[
          generateWebPageSchema({
            type: "CollectionPage",
            title: seoTitle,
            description: seoDescription,
            url: canonicalUrl,
            aboutName: "Substance Use Disorder Treatment",
            aboutDescription:
              "Treatment centers, recovery housing, peer support, and crisis services for addiction recovery in New Hampshire",
            audienceType:
              "People seeking addiction recovery treatment in New Hampshire",
            speciality: "Addiction Medicine",
          }),
          generateFAQSchema(faqSections.resources),
          directoryListSchema(filteredResources, canonicalUrl),
        ]}
      />

      <Nav />

      {/* Header Section */}
      <div className="bg-gray-800 border-b border-gray-600">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            {pageHeading}
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl">
            {showingCategoryLanding
              ? `Browse ${selectedCategory.toLowerCase()}${
                  showingCountyLanding ? ` in ${selectedCounty} County` : ""
                } and connect with recovery support across New Hampshire.`
              : seoDescription}
          </p>
        </div>
      </div>

      <nav aria-label="Browse resources by service or county" className="max-w-7xl mx-auto px-4 py-6">
        <h2 className="text-xl font-bold text-cyan-400 mb-3">Browse by service or county</h2>
        <div className="flex flex-wrap gap-3 mb-4">
          {resourceCategories.map((category) => (
            <a key={category} href={getResourceCategoryUrl(category)} className="text-cyan-400 hover:underline">{category}</a>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          {counties.filter((county) => county !== "All").map((county) => (
            <a key={county} href={showingCategoryLanding ? getResourceCategoryCountyUrl(selectedCategory, county) : getResourceCountyUrl(county)} className="text-cyan-400 hover:underline">{county} County</a>
          ))}
        </div>
        <p className="text-gray-400 text-sm mt-4">Contact providers to confirm current services, eligibility, costs, insurance, and availability. <a href="/contact" className="text-cyan-400 hover:underline">Report an outdated listing or suggest a resource.</a></p>
      </nav>

      {/* Filters Section */}
      <div className="bg-gray-800 border-b border-gray-600 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search resources, services, or organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-700 border-2 border-gray-600 text-white px-4 py-3 pr-10 rounded-lg focus:border-cyan-500 focus:outline-none placeholder-gray-400"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* County Filter */}
            <div className="w-full lg:w-48">
              <select
                value={selectedCounty}
                onChange={(e) => setSelectedCounty(e.target.value)}
                className="w-full bg-gray-700 border-2 border-gray-600 text-white px-4 py-3 rounded-lg focus:border-cyan-500 focus:outline-none"
              >
                {counties.map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="w-full lg:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-gray-700 border-2 border-gray-600 text-white px-4 py-3 rounded-lg focus:border-cyan-500 focus:outline-none"
              >
                <option value="All">All Categories</option>
                {resourceCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-center">
            <span className="text-gray-400 text-sm">
              Showing {filteredResources.length} of {resources.length} resources
              {selectedCounty !== "All" && ` in ${selectedCounty} County`}
              {selectedCategory !== "All" && ` for ${selectedCategory}`}
            </span>
            {shouldUseCollapsibleCategories &&
              Object.keys(groupedResources).length > 1 && (
                <div className="mt-2">
                  <span className="text-cyan-400 text-sm">
                    Click category and subcategory headers to expand/collapse
                    sections
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Resources Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {Object.keys(groupedResources).length === 0 ? (
          // No Results
          <div className="text-center py-12">
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-8 max-w-md mx-auto">
              <h3 className="text-xl font-bold text-white mb-2">
                No Resources Found
              </h3>
              <p className="text-gray-400 mb-4">
                Try adjusting your search criteria or filters to find resources.
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCounty("All");
                  setSelectedCategory("All");
                }}
                className="bg-cyan-500 hover:bg-cyan-400 text-gray-900 px-4 py-2 rounded font-semibold transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        ) : (
          // Results by Category
          Object.entries(groupedResources).map(([category, subcategories]) => {
            const isCategoryExpanded = expandedCategories.has(category);
            const categoryResourceCount =
              Object.values(subcategories).flat().length;

            return (
              <div key={category} className="mb-12">
                {/* Category Header - Clickable when using collapsible mode */}
                <div className="mb-6">
                  {shouldUseCollapsibleCategories ? (
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full text-left group focus:outline-none"
                    >
                      <div className="flex items-center justify-between border-b-2 border-cyan-500 pb-2 group-hover:border-cyan-400 transition-colors">
                        <h2 className="text-3xl font-bold text-cyan-400 group-hover:text-cyan-300 transition-colors">
                          {category.toUpperCase()}
                        </h2>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 text-sm">
                            {categoryResourceCount} resource
                            {categoryResourceCount !== 1 ? "s" : ""}
                          </span>
                          <div className="flex items-center">
                            <svg
                              className={`w-6 h-6 text-cyan-400 transition-transform duration-200 ${
                                isCategoryExpanded ? "rotate-90" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <h2 className="text-3xl font-bold text-cyan-400 mb-2 border-b-2 border-cyan-500 pb-2">
                      {category.toUpperCase()}
                    </h2>
                  )}
                </div>

                {/* Category Content - Show if not collapsible or if expanded */}
                {(!shouldUseCollapsibleCategories || isCategoryExpanded) && (
                  <div className="animate-in slide-in-from-top-2 duration-200">
                    {/* Subcategories - Sort so named subcategories come first, then general resources */}
                    {Object.entries(subcategories)
                      .sort(([a], [b]) => {
                        // Put empty string (no subcategory) last
                        if (a === "" && b !== "") return 1;
                        if (a !== "" && b === "") return -1;
                        // Alphabetical for named subcategories
                        return a.localeCompare(b);
                      })
                      .map(([subcategory, resourceList]) => {
                        const categorySubcategoryKey = `${category}-${
                          subcategory || "no-sub"
                        }`;
                        const guidance =
                          subcategoryGuidance[category]?.[subcategory];
                        const isSubcategoryExpanded = expandedSubcategories.has(
                          categorySubcategoryKey
                        );
                        const hasSubcategories =
                          category === "Housing" ||
                          category === "Primary SUD Providers";
                        const shouldCollapseSubcategory =
                          (shouldUseCollapsibleCategories &&
                            hasSubcategories &&
                            subcategory) ||
                          (category === "Primary SUD Providers" && subcategory);

                        return (
                          <div key={categorySubcategoryKey} className="mb-8">
                            {subcategory && // Only render subcategory header if it exists
                              (shouldCollapseSubcategory ? (
                                <button
                                  onClick={() =>
                                    toggleSubcategory(categorySubcategoryKey)
                                  }
                                  className="w-full text-left group focus:outline-none mb-4"
                                >
                                  <div className="flex items-center justify-between border-l-4 border-cyan-500 pl-4 pr-2 py-2 hover:bg-gray-800 rounded-r transition-colors">
                                    <h3 className="text-xl font-semibold text-white group-hover:text-cyan-300 transition-colors">
                                      {getSubcategoryDisplayName(subcategory)}
                                    </h3>
                                    <div className="flex items-center gap-3">
                                      <span className="text-gray-400 text-sm">
                                        {resourceList.length} resource
                                        {resourceList.length !== 1 ? "s" : ""}
                                      </span>
                                      <div className="flex items-center">
                                        <svg
                                          className={`w-5 h-5 text-cyan-400 transition-transform duration-200 ${
                                            isSubcategoryExpanded
                                              ? "rotate-90"
                                              : ""
                                          }`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ) : (
                                <h3 className="text-xl font-semibold text-white mb-4 border-l-4 border-cyan-500 pl-4">
                                  {getSubcategoryDisplayName(subcategory)}
                                </h3>
                              ))}

                            {/* Resource Cards Grid - Show if no subcategory, not collapsible, or expanded */}
                            {(!shouldCollapseSubcategory ||
                              !subcategory ||
                              isSubcategoryExpanded) && (
                              <>
                                {guidance && (
                                  <aside
                                    className="mb-4 rounded-lg border border-cyan-800 border-l-4 border-l-cyan-400 bg-gray-800 px-4 py-3"
                                    aria-label={guidance.title}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div
                                        className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cyan-500 text-sm font-bold text-gray-900"
                                        aria-hidden="true"
                                      >
                                        i
                                      </div>
                                      <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-cyan-400">
                                          About this service
                                        </p>
                                        <h4 className="mt-0.5 font-semibold text-white">
                                          {guidance.title}
                                        </h4>
                                        <p className="mt-1 text-sm leading-relaxed text-gray-300">
                                          {guidance.description}
                                        </p>
                                        {guidance.link && (
                                          <a
                                            href={guidance.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-2 inline-flex items-center text-sm font-semibold text-cyan-400 hover:text-cyan-300 hover:underline"
                                          >
                                            {guidance.linkLabel}
                                            <span
                                              className="ml-1"
                                              aria-hidden="true"
                                            >
                                              &rarr;
                                            </span>
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </aside>
                                )}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                  {resourceList
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((resource) => (
                                      <ResourceCard
                                        key={resource.id}
                                        resource={resource}
                                      />
                                    ))}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer CTA */}
      <div className="bg-gray-800 border-t border-gray-600 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <h3 className="text-xl font-bold text-white mb-2">
            Need Help Finding Resources?
          </h3>
          <p className="text-gray-400 mb-4">
            If you can't find what you're looking for, or need immediate
            assistance, contact the crisis line.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:211"
              className="bg-cyan-500 hover:bg-cyan-400 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Call 2-1-1 for Help
            </a>
            <a
              href="/contact"
              className="border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="border-b-2 border-cyan-500 pb-3 mb-8">
          <h2 className="text-3xl font-bold text-white">
            RECOVERY RESOURCES FAQ
          </h2>
        </div>
        <div className="space-y-6">
          {faqSections.resources.map((faq) => (
            <article
              key={faq.question}
              className="bg-gray-800 border border-gray-600 rounded-lg p-6"
            >
              <h3 className="text-xl font-semibold text-cyan-400 mb-3">
                {faq.question}
              </h3>
              <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-600 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">CONTACT US</h5>
              <p className="text-sm text-gray-300">
                <br />
                Laconia, NH 03246
                <br />
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">SECTIONS</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href="/articles/category/unsung-heroes"
                    className="hover:text-cyan-400"
                  >
                    Healthcare Heroes
                  </a>
                </li>
                <li>
                  <a
                    href="/articles/category/stories-of-recovery"
                    className="hover:text-cyan-400"
                  >
                    Recovery Stories
                  </a>
                </li>
                <li>
                  <a href="/resources" className="hover:text-cyan-400">
                    Resources
                  </a>
                </li>
                <li>
                  <a
                    href="/articles/category/blog-posts"
                    className="hover:text-cyan-400"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">RESOURCES</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href="/resources/category/treatment-centers"
                    className="hover:text-cyan-400"
                  >
                    Treatment Centers
                  </a>
                </li>
                <li>
                  <a
                    href="/resources/category/support-groups"
                    className="hover:text-cyan-400"
                  >
                    Support Groups
                  </a>
                </li>
                <li>
                  <a
                    href="/resources/category/crisis-services"
                    className="hover:text-cyan-400"
                  >
                    Crisis Hotline
                  </a>
                </li>
                <li>
                  <a
                    href="/resources/category/mental-health-providers"
                    className="hover:text-cyan-400"
                  >
                    Mental Health
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">FOLLOW US</h5>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61559004021648"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Recovery Compass NH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
